import { Suggestion } from '@tunasong/icons'
import { getChordConfidenceClassName } from '@tunasong/music-ui'
import { Table, TableBody, TableCell, TableRow, Typography } from '@tunasong/ui-lib'
import { getPlugin, useEditor, usePlugins } from '@tunasong/plugin-lib'
import { useSuggestions } from '@tunasong/plugin-suggestion/ui'
import { useMusicContext } from '@tunasong/redux'
import type { ElementType } from '@tunasong/schemas'
import type { SidebarComponent } from '@tunasong/ui-lib'
import type { FC } from 'react'
import styles from './sidebar-suggestion.module.css'

export const SidebarSuggestion: FC<SidebarComponent> = ({}) => {
  const editor = useEditor({ allowNull: true })

  const { suggestions, loading } = useSuggestions(editor)
  const plugins = usePlugins('all')
  const [hasScale] = useMusicContext({ editorId: editor?.id, selector: state => Boolean(state.scale?.current) })

  if (!suggestions) {
    return null
  }

  const isEmpty = suggestions.length === 0
  const emptyMessage = hasScale
    ? 'No theory-backed suggestions here.'
    : 'Set a key to see chord suggestions.'

  return (
    <Table>
      <TableBody>
        {suggestions.map(({ name, type: suggestionType, description, score, chordAnalysis }, idx) => {
          /** @todo suggestion type and plugin type are different, although they may overlap... */
          const type = suggestionType as ElementType
          const Icon = getPlugin({ type }, plugins)?.icon
          const confidenceClassName = chordAnalysis ? getChordConfidenceClassName(chordAnalysis.confidence) : undefined
          return (
            <TableRow key={idx}>
              <TableCell>{Icon ? <Icon /> : null}</TableCell>
              <TableCell>{name}</TableCell>
              <TableCell>
                {chordAnalysis ? (
                  <span className={confidenceClassName}>{chordAnalysis.chordFunction}</span>
                ) : (
                  ''
                )}
              </TableCell>
              <TableCell>
                {chordAnalysis ? (
                  <span className={confidenceClassName}>{chordAnalysis.description}</span>
                ) : (
                  description ?? ''
                )}
              </TableCell>
              <TableCell>{(Math.round(score * 100) / 100).toFixed(2)}</TableCell>
            </TableRow>
          )
        })}

        {isEmpty && !loading && (
          <TableRow>
            <TableCell colSpan={5} className={styles.emptyCell}>
              <div className={styles.emptyState} role="status">
                <Suggestion size={32} className={styles.emptyIcon} aria-hidden="true" />
                <Typography variant="body2">{emptyMessage}</Typography>
              </div>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}
